/**
 * Единая точка правды для URL'ов подложки карты.
 *
 * ЗАЧЕМ (перф-аудит карты 14.08, корень 1):
 * фронт ходил за спутниковыми тайлами НАПРЯМУЮ на `server.arcgisonline.com`.
 * У клиента в поле это давало до 5.6 c на тайл, а гибрид (imagery + transport +
 * places) выдавал до ~150 внешних запросов на один экран — три TLS-хендшейка к
 * заокеанскому CDN на каждый слой, без общего кэша между пользователями.
 *
 * При этом наш nginx-прокси `/tiles/imagery/` (и зеркальные `/tiles/transport/`,
 * `/tiles/places/`) на проде УЖЕ жил: 30-дневный edge-кэш, warm 0.009 c,
 * cold 0.25 c — и просто не использовался. Здесь мы его включаем.
 *
 * Fallback на прямой arcgisonline НЕ делаем сознательно: прокси живёт на нашем
 * же домене за тем же nginx, что отдаёт API и сам SPA. Если он недоступен —
 * недоступно приложение целиком, отдельная деградация подложки смысла не имеет
 * (и вернула бы ту самую 5.6-секундную зависимость от внешнего CDN).
 *
 * Конфиг локаций — `deploy/nginx/aikolxoz.com.conf` и
 * `deploy/nginx/staging.aikolxoz.com.conf`. Порядок осей у Esri — `{z}/{y}/{x}`
 * (НЕ `{z}/{x}/{y}`), regex локации назван так же; менять местами нельзя.
 */

/** Спутниковая подложка (Esri World Imagery) через наш кэширующий прокси. */
export const IMAGERY_TILE_PATH = '/tiles/imagery/{z}/{y}/{x}';

/** Дороги без подписей (Esri World Transportation), прозрачный фон. */
export const TRANSPORT_TILE_PATH = '/tiles/transport/{z}/{y}/{x}';

/** Границы + подписи населённых пунктов (Esri World Boundaries and Places). */
export const PLACES_TILE_PATH = '/tiles/places/{z}/{y}/{x}';

/**
 * OSM остаётся внешним: своей прокси-локации под него нет, и заводить её без
 * согласования с OSM Tile Usage Policy нельзя. Подложка «Карта» — не основной
 * режим работы агронома (основной — спутник/гибрид), поэтому в перф-пакет она
 * не входит.
 */
export const OSM_STREETS_PATH = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/**
 * Абсолютный same-origin URL тайла подложки.
 *
 * MapLibre принимает и относительный путь, но весь остальной код карты
 * (MVT-источники, NDVI-растры) строит URL через `window.location.origin` —
 * держим одну конвенцию, чтобы источники были одинаково узнаваемы в devtools
 * и в тестах. Хост всегда наш: внешних доменов в подложке больше нет.
 */
export function basemapTileUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

/** Готовые URL'ы — то, что уходит в `sources.tiles[]`. */
export const IMAGERY_TILE_URL = (): string => basemapTileUrl(IMAGERY_TILE_PATH);
export const TRANSPORT_TILE_URL = (): string => basemapTileUrl(TRANSPORT_TILE_PATH);
export const PLACES_TILE_URL = (): string => basemapTileUrl(PLACES_TILE_PATH);
